import type { ConfiguratorDefinition } from "~/Configurators/common/v2/StepDefinition";
import { RESET_SELECTION, RESET_STANDARD } from "~/Configurators/common/advancedTranslationUtils";
import * as CarriageCstic from "./CarriageCstics";
import { SubstepType } from "~/Configurators/Linear/dtos";
import { userIsCustomer } from "~/utils/roles";

import { getCsticPossibleValues, getCsticsValue } from "~/Configurators/common/v2/helper";

export const CarriagesDefinition: ConfiguratorDefinition = {
  description: [
    {
      condition: ({ mainKmat }) => {
        return mainKmat.cstics[CarriageCstic.MaterialName]?.value === "RWU";
      },
      value: "configurator.carriage.summary.RWU.description",
    },
    {
      condition: ({ mainKmat }) => {
        return mainKmat.cstics[CarriageCstic.MaterialName]?.value === "KWSE";
      },
      value: "configurator.carriage.summary.KWSE.description",
    },
    {
      condition: ({ mainKmat }) => {
        return mainKmat.cstics[CarriageCstic.MaterialName]?.value === "KWVE";
      },
      value: "configurator.carriage.summary.KWVE.description",
    },
    {
      condition: ({ mainKmat }) => {
        return mainKmat.cstics[CarriageCstic.MaterialName]?.value === "KWVE..-W";
      },
      value: "configurator.carriage.summary.KWVE-W.description",
    },
  ],
  mediaUrl: [
    {
      condition: ({ mainKmat }) => {
        return mainKmat.cstics[CarriageCstic.MaterialName]?.value === "RWU";
      },
      value: "configurator.carriage.summary.RWU.image",
    },
    {
      condition: ({ mainKmat }) => {
        return mainKmat.cstics[CarriageCstic.MaterialName]?.value === "KWSE";
      },
      value: "configurator.carriage.summary.KWSE.image",
    },
    {
      condition: ({ mainKmat }) => {
        return mainKmat.cstics[CarriageCstic.MaterialName]?.value === "KWVE";
      },
      value: "configurator.carriage.summary.KWVE.image",
    },
    {
      condition: ({ mainKmat }) => {
        return mainKmat.cstics[CarriageCstic.MaterialName]?.value === "KWVE..-W";
      },
      value: "configurator.carriage.summary.KWVE-W.image",
    },
  ],
  pages: [
    {
      name: "configurator.carriage.page.1.name",
      enabled: true,
      resetLabel: RESET_SELECTION,
      resetRequiresConfirmation: false,
      substeps: [
        {
          type: SubstepType.Series,

          name: "configurator.carriage.step.series.name",
          description: "configurator.carriage.step.series.description",

          tiles: [
            {
              name: "RWU",
              description: "configurator.carriage.cstic.series.RWU.description",
              imageUrl: "configurator.carriage.cstic.series.RWU.image",
              info: "configurator.carriage.cstic.series.RWU.info",
              cstics: [
                {
                  csticName: CarriageCstic.MaterialName,
                  value: "RWU",
                },
              ],
            },
            {
              name: "KWSE",
              description: "configurator.carriage.cstic.series.KWSE.description",
              imageUrl: "configurator.carriage.cstic.series.KWSE.image",
              info: "configurator.carriage.cstic.series.KWSE.info",
              cstics: [
                {
                  csticName: CarriageCstic.MaterialName,
                  value: "KWSE",
                },
              ],
            },
            {
              name: "KWVE",
              description: "configurator.carriage.cstic.series.KWVE.description",
              imageUrl: "configurator.carriage.cstic.series.KWVE.image",
              info: "configurator.carriage.cstic.series.KWVE.info",
              cstics: [
                {
                  csticName: CarriageCstic.MaterialName,
                  value: "KWVE",
                },
              ],
            },
            {
              name: "KWVE..-W",
              description: "configurator.carriage.cstic.series.KWVE-W.description",
              imageUrl: "configurator.carriage.cstic.series.KWVE-W.image",
              info: "configurator.carriage.cstic.series.KWVE-W.info",
              cstics: [
                {
                  csticName: CarriageCstic.MaterialName,
                  value: "KWVE..-W",
                },
              ],
            },
          ],
          tilesPerRow: 4,

          csticsToReset: [
            CarriageCstic.MaterialName,
            CarriageCstic.CarriageDesign,
            CarriageCstic.Accuracy,
            CarriageCstic.Preload,
          ],

          visible: true,
        },
        {
          type: SubstepType.Size,

          name: "configurator.carriage.step.size.name",
          description: "configurator.carriage.step.size.description",

          tiles: [
            {
              name: "15",
              cstics: [
                {
                  csticName: CarriageCstic.SizeCode,
                  value: "15",
                },
              ],
            },
            {
              name: "20",
              cstics: [
                {
                  csticName: CarriageCstic.SizeCode,
                  value: "20",
                },
              ],
            },
            {
              name: "25",
              cstics: [
                {
                  csticName: CarriageCstic.SizeCode,
                  value: "25",
                },
              ],
            },
            {
              name: "30",
              cstics: [
                {
                  csticName: CarriageCstic.SizeCode,
                  value: "30",
                },
              ],
            },
            {
              name: "35",
              cstics: [
                {
                  csticName: CarriageCstic.SizeCode,
                  value: "35",
                },
              ],
            },
            {
              name: "45",
              cstics: [
                {
                  csticName: CarriageCstic.SizeCode,
                  value: "45",
                },
              ],
            },
            {
              name: "55",
              cstics: [
                {
                  csticName: CarriageCstic.SizeCode,
                  value: "55",
                },
              ],
            },
            {
              name: "65",
              cstics: [
                {
                  csticName: CarriageCstic.SizeCode,
                  value: "65",
                },
              ],
            },
            {
              name: "100",
              cstics: [
                {
                  csticName: CarriageCstic.SizeCode,
                  value: "100",
                },
              ],
            },
          ],
          tilesPerRow: 12,

          csticsToReset: [CarriageCstic.SizeCode],

          visible: true,
        },
      ],
    },
    {
      name: "configurator.carriage.page.2.name",
      enabled: true,
      resetLabel: RESET_STANDARD,
      resetRequiresConfirmation: true,
      substeps: [
        {
          type: SubstepType.Coating,

          name: "configurator.carriage.step.coating.name",
          description: "configurator.carriage.step.coating.description",
          info: "configurator.carriage.step.coating.info",
          footerInfo: [
            [
              {
                condition: ({ mainKmat }) => {
                  const [materialName, size, coating, accuracy] = getCsticsValue(
                    mainKmat,
                    CarriageCstic.MaterialName,
                    CarriageCstic.SizeCode,
                    CarriageCstic.Coating,
                    CarriageCstic.Accuracy,
                  );
                  return (
                    ["RWU"].includes(materialName) &&
                    ["KD", "RROC"].includes(coating) &&
                    ((["25", "35", "45", "55"].includes(size) && ["G0", "G1", "G2", "G3"].includes(accuracy)) ||
                      (["65", "100"].includes(size) && accuracy === "G2"))
                  );
                },
                value: "configurator.carriage.step.accuracy.footerInfo",
              },
            ],
            [
              {
                condition: ({ mainKmat }) => {
                  const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                  return ["KWSE", "KWVE"].includes(materialName);
                },
                value: "configurator.carriage.step.coating.footerInfo.preload",
              },
            ],
            "configurator.carriage.step.coating.footerInfo.default",
          ],

          tiles: [
            {
              name: "configurator.carriage.cstic.coating./.name",
              cstics: [
                {
                  csticName: CarriageCstic.Coating,
                  value: "/",
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.coating.RROC.name",
              description: "configurator.carriage.cstic.coating.RROC.description",
              info: "configurator.carriage.cstic.coating.RROC.info",
              cstics: [
                {
                  csticName: CarriageCstic.Coating,
                  value: "RROC",
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.coating.KD.name",
              description: "configurator.carriage.cstic.coating.KD.description",
              info: "configurator.carriage.cstic.coating.KD.info",
              cstics: [
                {
                  csticName: CarriageCstic.Coating,
                  value: "KD",
                },
              ],
            },
          ],
          tilesPerRow: 4,

          csticsToReset: [CarriageCstic.Coating],

          visible: true,
        },
        {
          type: SubstepType.Preload,

          name: "configurator.carriage.step.preload.name",
          description: "configurator.carriage.step.preload.description",
          info: [
            {
              condition: ({ mainKmat }) => {
                const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                return ["RWU"].includes(materialName);
              },
              value: "configurator.carriage.step.preload.info.RWU",
            },
            {
              condition: ({ mainKmat }) => {
                const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                return ["KWSE"].includes(materialName);
              },
              value: "configurator.carriage.step.preload.info.KWSE",
            },
            {
              condition: ({ mainKmat }) => {
                const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                return ["KWVE", "KWVE..-W"].includes(materialName);
              },
              value: "configurator.carriage.step.preload.info.KWVE",
            },
          ],

          tiles: [
            {
              name: "V0",
              cstics: [
                {
                  csticName: CarriageCstic.Preload,
                  value: "V0",
                },
              ],
            },
            {
              name: "V1",
              cstics: [
                {
                  csticName: CarriageCstic.Preload,
                  value: "V1",
                },
              ],
            },
            {
              name: "V2",
              cstics: [
                {
                  csticName: CarriageCstic.Preload,
                  value: "V2",
                },
              ],
            },
            {
              name: "V3",
              cstics: [
                {
                  csticName: CarriageCstic.Preload,
                  value: "V3",
                },
              ],
            },
            {
              name: "V4",
              cstics: [
                {
                  csticName: CarriageCstic.Preload,
                  value: "V4",
                },
              ],
            },
            {
              name: "V5",
              cstics: [
                {
                  csticName: CarriageCstic.Preload,
                  value: "V5",
                },
              ],
            },
          ],
          tilesPerRow: 6,

          csticsToReset: [CarriageCstic.Preload],

          visible: true,
        },
        {
          type: SubstepType.Accuracy,

          name: "configurator.carriage.step.accuracy.name",
          description: "configurator.carriage.step.accuracy.description",
          info: [
            {
              condition: ({ mainKmat }) => {
                const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                return ["RWU"].includes(materialName);
              },
              value: "configurator.carriage.step.accuracy.info.RWU",
            },
            {
              condition: ({ mainKmat }) => {
                const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                return ["KWSE"].includes(materialName);
              },
              value: "configurator.carriage.step.accuracy.info.KWSE",
            },
            {
              condition: ({ mainKmat }) => {
                const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                return ["KWVE", "KWVE..-W"].includes(materialName);
              },
              value: "configurator.carriage.step.accuracy.info.KWVE",
            },
          ],

          tiles: [
            {
              name: "G0",
              cstics: [
                {
                  csticName: CarriageCstic.Accuracy,
                  value: "G0",
                },
              ],
            },
            {
              name: "G1",
              cstics: [
                {
                  csticName: CarriageCstic.Accuracy,
                  value: "G1",
                },
              ],
            },
            {
              name: "G2",
              cstics: [
                {
                  csticName: CarriageCstic.Accuracy,
                  value: "G2",
                },
              ],
            },
            {
              name: "G3",
              cstics: [
                {
                  csticName: CarriageCstic.Accuracy,
                  value: "G3",
                },
              ],
            },
            {
              name: "G4",
              cstics: [
                {
                  csticName: CarriageCstic.Accuracy,
                  value: "G4",
                },
              ],
            },
          ],
          tilesPerRow: 6,

          footerInfo: [
            [
              {
                condition: ({ mainKmat }) => {
                  const [series, size, accuracy, coating] = getCsticsValue(
                    mainKmat,
                    CarriageCstic.MaterialName,
                    CarriageCstic.SizeCode,
                    CarriageCstic.Accuracy,
                    CarriageCstic.Coating,
                  );
                  return (
                    (series === "RWU" &&
                      ["25", "35", "45", "55"].includes(size) &&
                      ["G0", "G1"].includes(accuracy) &&
                      ["RROC", "KD", "/"].includes(coating)) ||
                    (series === "RWU" &&
                      ["65", "100"].includes(size) &&
                      accuracy === "G3" &&
                      ["KD", "RROC"].includes(coating)) ||
                    (series === "RWU" &&
                      ["65", "100"].includes(size) &&
                      ["G0", "G1", "G2"].includes(accuracy) &&
                      coating === "/")
                  );
                },
                value: "configurator.carriage.step.accuracy.footerInfo",
              },
            ],
          ],

          csticsToReset: [CarriageCstic.Accuracy],

          visible: true,
        },
        {
          type: SubstepType.CarriageType,

          name: "configurator.carriage.step.carriageType.name",
          description: "configurator.carriage.step.carriageType.description",

          tiles: [
            {
              name: "configurator.carriage.cstic.carriageType./.name",
              imageUrl: "configurator.carriage.cstic.carriageType./.image",
              cstics: [
                {
                  csticName: CarriageCstic.CarriageDesign,
                  value: "/",
                },
              ],
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                    return !["KWVE..-W"].includes(materialName);
                  },
                  value: true,
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.carriageType.L.name",
              imageUrl: "configurator.carriage.cstic.carriageType.L.image",
              cstics: [
                {
                  csticName: CarriageCstic.CarriageDesign,
                  value: "L",
                },
              ],
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                    return !["KWVE..-W"].includes(materialName);
                  },
                  value: true,
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.carriageType.S.name",
              imageUrl: "configurator.carriage.cstic.carriageType.S.image",
              cstics: [
                {
                  csticName: CarriageCstic.CarriageDesign,
                  value: "S",
                },
              ],
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                    return !["KWVE..-W"].includes(materialName);
                  },
                  value: true,
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.carriageType.SL.name",
              imageUrl: "configurator.carriage.cstic.carriageType.SL.image",
              cstics: [
                {
                  csticName: CarriageCstic.CarriageDesign,
                  value: "SL",
                },
              ],
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                    return !["KWVE..-W"].includes(materialName);
                  },
                  value: true,
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.carriageType.E.name",
              imageUrl: "configurator.carriage.cstic.carriageType.E.image",
              cstics: [
                {
                  csticName: CarriageCstic.CarriageDesign,
                  value: "E",
                },
              ],
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                    return !["KWVE..-W"].includes(materialName);
                  },
                  value: true,
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.carriageType.EC.name",
              imageUrl: "configurator.carriage.cstic.carriageType.EC.image",
              cstics: [
                {
                  csticName: CarriageCstic.CarriageDesign,
                  value: "EC",
                },
              ],
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                    return !["KWVE..-W"].includes(materialName);
                  },
                  value: true,
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.carriageType.ES.name",
              imageUrl: "configurator.carriage.cstic.carriageType.ES.image",
              cstics: [
                {
                  csticName: CarriageCstic.CarriageDesign,
                  value: "ES",
                },
              ],
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                    return !["KWVE..-W"].includes(materialName);
                  },
                  value: true,
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.carriageType.ESC.name",
              imageUrl: "configurator.carriage.cstic.carriageType.ESC.image",
              cstics: [
                {
                  csticName: CarriageCstic.CarriageDesign,
                  value: "ESC",
                },
              ],
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                    return !["KWVE..-W"].includes(materialName);
                  },
                  value: true,
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.carriageType.H.name",
              imageUrl: "configurator.carriage.cstic.carriageType.H.image",
              cstics: [
                {
                  csticName: CarriageCstic.CarriageDesign,
                  value: "H",
                },
              ],
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                    return !["KWVE..-W"].includes(materialName);
                  },
                  value: true,
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.carriageType.HL.name",
              imageUrl: "configurator.carriage.cstic.carriageType.HL.image",
              cstics: [
                {
                  csticName: CarriageCstic.CarriageDesign,
                  value: "HL",
                },
              ],
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                    return !["KWVE..-W"].includes(materialName);
                  },
                  value: true,
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.carriageType.N.name",
              imageUrl: "configurator.carriage.cstic.carriageType.N.image",
              cstics: [
                {
                  csticName: CarriageCstic.CarriageDesign,
                  value: "N",
                },
              ],
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                    return !["KWVE..-W"].includes(materialName);
                  },
                  value: true,
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.carriageType.NL.name",
              imageUrl: "configurator.carriage.cstic.carriageType.NL.image",
              cstics: [
                {
                  csticName: CarriageCstic.CarriageDesign,
                  value: "NL",
                },
              ],
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                    return !["KWVE..-W"].includes(materialName);
                  },
                  value: true,
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.carriageType.SN.name",
              imageUrl: "configurator.carriage.cstic.carriageType.SN.image",
              cstics: [
                {
                  csticName: CarriageCstic.CarriageDesign,
                  value: "SN",
                },
              ],
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                    return !["KWVE..-W"].includes(materialName);
                  },
                  value: true,
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.carriageType.SNL.name",
              imageUrl: "configurator.carriage.cstic.carriageType.SNL.image",
              cstics: [
                {
                  csticName: CarriageCstic.CarriageDesign,
                  value: "SNL",
                },
              ],
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                    return !["KWVE..-W"].includes(materialName);
                  },
                  value: true,
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.carriageType.W.name",
              imageUrl: "configurator.carriage.cstic.carriageType.W.image",
              cstics: [
                {
                  csticName: CarriageCstic.CarriageDesign,
                  value: "W",
                },
              ],
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                    return ["KWVE..-W"].includes(materialName);
                  },
                  value: true,
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.carriageType.WL.name",
              imageUrl: "configurator.carriage.cstic.carriageType.WL.image",
              cstics: [
                {
                  csticName: CarriageCstic.CarriageDesign,
                  value: "WL",
                },
              ],
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                    return ["KWVE..-W"].includes(materialName);
                  },
                  value: true,
                },
              ],
            },
          ],
          tilesPerRow: 4,

          csticsToReset: [CarriageCstic.CarriageDesign],

          visible: true,
        },
      ],
    },
    {
      name: "configurator.carriage.page.3.name",
      enabled: true,
      resetLabel: RESET_STANDARD,
      resetRequiresConfirmation: true,
      substeps: [
        {
          type: SubstepType.Lubrication,
          name: "configurator.carriage.step.lubrication.name",
          description: "configurator.carriage.step.lubrication.description",
          info: "configurator.carriage.step.lubrication.info",
          tiles: [
            {
              name: "configurator.carriage.cstic.lubrication.ungreased.name",
              description: "configurator.carriage.cstic.lubrication.ungreased.description",
              cstics: [
                [
                  {
                    condition: ({ mainKmat }) => {
                      const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                      return ["RWU", "KWSE"].includes(materialName);
                    },
                    value: {
                      csticName: CarriageCstic.GreasingPerGreaseGroup,
                      value: "/",
                    },
                  },
                  {
                    condition: ({ mainKmat }) => {
                      const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                      return ["KWVE", "KWVE..-W"].includes(materialName);
                    },
                    value: {
                      csticName: CarriageCstic.GreasingPerGreaseGroup,
                      value: "UG",
                    },
                  },
                ],
                {
                  csticName: CarriageCstic.GreasingLType,
                  value: "/",
                },
              ],
              overrideCsticsToEnable: true,
            },
            {
              name: "configurator.carriage.cstic.lubrication.basic.name",
              description: "configurator.carriage.cstic.lubrication.basic.description",
              cstics: [
                {
                  csticName: CarriageCstic.GreasingPerGreaseGroup,
                  value: "/",
                },
                {
                  csticName: CarriageCstic.GreasingLType,
                  value: "/",
                },
              ],
              overrideCsticsToEnable: true,
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                    return ["KWVE", "KWVE..-W"].includes(materialName);
                  },
                  value: true,
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.lubrication.initial.name",
              cstics: [
                [
                  {
                    condition: ({ mainKmat }) => {
                      const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                      return ["KWSE"].includes(materialName);
                    },
                    value: {
                      csticName: CarriageCstic.GreasingPerGreaseGroup,
                      value: "GA18",
                    },
                  },
                  {
                    condition: ({ mainKmat }) => {
                      const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                      return ["RWU"].includes(materialName);
                    },
                    value: {
                      csticName: CarriageCstic.GreasingPerGreaseGroup,
                      value: "GA31",
                    },
                  },
                ],
                {
                  csticName: CarriageCstic.GreasingLType,
                  value: "/",
                },
              ],
              overrideCsticsToEnable: true,
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [materialName] = getCsticsValue(mainKmat, CarriageCstic.MaterialName);
                    return ["RWU", "KWSE"].includes(materialName);
                  },
                  value: true,
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.lubrication.special.name",
              description: "configurator.carriage.cstic.lubrication.special.description",
              cstics: [
                {
                  csticName: CarriageCstic.GreasingPerGreaseGroup,
                  value: "SF",
                },
                {
                  csticName: CarriageCstic.GreasingLType,
                  value: "",
                },
              ],
              overrideCsticsToEnable: true,
            },
          ],
          groupedTiles: [
            {
              tiles: [
                {
                  name: "configurator.carriage.cstic.lubrication.special.L077.name",
                  cstics: [
                    {
                      csticName: CarriageCstic.GreasingPerGreaseGroup,
                      value: "SF",
                    },
                    {
                      csticName: CarriageCstic.GreasingLType,
                      value: "L077",
                    },
                  ],
                  visible: [
                    {
                      condition: ({ mainKmat }) => {
                        const values = getCsticPossibleValues(mainKmat, CarriageCstic.GreasingLType);
                        return values.includes("L077");
                      },
                      value: true,
                    },
                  ],
                },

                {
                  name: "configurator.carriage.cstic.lubrication.special.L271.name",
                  cstics: [
                    {
                      csticName: CarriageCstic.GreasingPerGreaseGroup,
                      value: "SF",
                    },
                    {
                      csticName: CarriageCstic.GreasingLType,
                      value: "L271",
                    },
                  ],
                  visible: [
                    {
                      condition: ({ mainKmat }) => {
                        const values = getCsticPossibleValues(mainKmat, CarriageCstic.GreasingLType);
                        return values.includes("L271");
                      },
                      value: true,
                    },
                  ],
                },
                {
                  name: "configurator.carriage.cstic.lubrication.special.L409.name",
                  cstics: [
                    {
                      csticName: CarriageCstic.GreasingPerGreaseGroup,
                      value: "SF",
                    },
                    {
                      csticName: CarriageCstic.GreasingLType,
                      value: "L409",
                    },
                  ],
                  visible: [
                    {
                      condition: ({ mainKmat }) => {
                        const values = getCsticPossibleValues(mainKmat, CarriageCstic.GreasingLType);
                        return values.includes("L409");
                      },
                      value: true,
                    },
                  ],
                },
                {
                  name: "configurator.carriage.cstic.lubrication.special.L430.name",
                  cstics: [
                    {
                      csticName: CarriageCstic.GreasingPerGreaseGroup,
                      value: "SF",
                    },
                    {
                      csticName: CarriageCstic.GreasingLType,
                      value: "L430",
                    },
                  ],
                  visible: [
                    {
                      condition: ({ mainKmat }) => {
                        const values = getCsticPossibleValues(mainKmat, CarriageCstic.GreasingLType);
                        return values.includes("L430");
                      },
                      value: true,
                    },
                  ],
                },
                {
                  name: "configurator.carriage.cstic.lubrication.special.L448.name",
                  cstics: [
                    {
                      csticName: CarriageCstic.GreasingPerGreaseGroup,
                      value: "SF",
                    },
                    {
                      csticName: CarriageCstic.GreasingLType,
                      value: "L448",
                    },
                  ],
                  visible: [
                    {
                      condition: ({ mainKmat }) => {
                        const values = getCsticPossibleValues(mainKmat, CarriageCstic.GreasingLType);
                        return values.includes("L448");
                      },
                      value: true,
                    },
                  ],
                },
                {
                  name: "configurator.carriage.cstic.lubrication.special.L460.name",
                  cstics: [
                    {
                      csticName: CarriageCstic.GreasingPerGreaseGroup,
                      value: "SF",
                    },
                    {
                      csticName: CarriageCstic.GreasingLType,
                      value: "L460",
                    },
                  ],
                  visible: [
                    {
                      condition: ({ mainKmat }) => {
                        const values = getCsticPossibleValues(mainKmat, CarriageCstic.GreasingLType);
                        return values.includes("L460");
                      },
                      value: true,
                    },
                  ],
                },
                {
                  name: "configurator.carriage.cstic.lubrication.special.L531.name",
                  cstics: [
                    {
                      csticName: CarriageCstic.GreasingPerGreaseGroup,
                      value: "SF",
                    },
                    {
                      csticName: CarriageCstic.GreasingLType,
                      value: "L531",
                    },
                  ],
                  visible: [
                    {
                      condition: ({ mainKmat }) => {
                        const values = getCsticPossibleValues(mainKmat, CarriageCstic.GreasingLType);
                        return values.includes("L531");
                      },
                      value: true,
                    },
                  ],
                },
              ],
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [greasingPerGreaseGroup] = getCsticsValue(mainKmat, CarriageCstic.GreasingPerGreaseGroup);
                    return ["SF"].includes(greasingPerGreaseGroup);
                  },
                  value: true,
                },
              ],
            },
          ],
          tilesPerRow: 4,
          csticsToReset: [CarriageCstic.GreasingPerGreaseGroup, CarriageCstic.GreasingLType],
          visible: true,
        },
        {
          type: SubstepType.SMDS,
          name: "configurator.carriage.step.SMDS.name",
          info: "configurator.carriage.step.SMDS.info",
          tiles: [
            {
              name: "configurator.carriage.cstic.SMDS.J.name",
              cstics: [
                {
                  csticName: CarriageCstic.SMDS,
                  value: "J",
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.SMDS.N.name",
              cstics: [
                {
                  csticName: CarriageCstic.SMDS,
                  value: "N",
                },
              ],
            },
          ],
          tilesPerRow: 6,
          csticsToReset: [CarriageCstic.SMDS],
          visible: [
            {
              condition: ({ mainKmat }) => {
                const [materialName, size] = getCsticsValue(
                  mainKmat,
                  CarriageCstic.MaterialName,
                  CarriageCstic.SizeCode,
                );
                return ["RWU"].includes(materialName) && ["35", "45", "55", "65"].includes(size);
              },
              value: true,
            },
          ],
        },
        {
          type: SubstepType.SMDE,
          name: "configurator.carriage.step.SMDE.name",
          info: "configurator.carriage.step.SMDE.info",
          tiles: [
            {
              name: "configurator.carriage.cstic.SMDE.ADE.name",
              cstics: [
                {
                  csticName: CarriageCstic.CoverStrip,
                  value: "ADE",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [greasing] = getCsticsValue(mainKmat, CarriageCstic.GreasingPerGreaseGroup);
                    return ["UG"].includes(greasing);
                  },
                  value: true,
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.SMDE.N.name",
              cstics: [
                {
                  csticName: CarriageCstic.CoverStrip,
                  value: "N",
                },
              ],
            },
          ],
          tilesPerRow: 6,
          csticsToReset: [CarriageCstic.CoverStrip],
          visible: [
            {
              condition: ({ mainKmat }) => {
                const [materialName, size, coverStrip, coating] = getCsticsValue(
                  mainKmat,
                  CarriageCstic.MaterialName,
                  CarriageCstic.SizeCode,
                  CarriageCstic.CoverStrip,
                  CarriageCstic.Coating,
                );
                return (
                  ["KWVE"].includes(materialName) &&
                  ["20", "25", "30"].includes(size) &&
                  ["N", "ADE"].includes(coverStrip) &&
                  !["KD"].includes(coating)
                );
              },
              value: true,
            },
          ],
        },
        {
          type: SubstepType.LocatingFace,
          name: "configurator.carriage.step.locatingFace.name",
          description: "configurator.carriage.step.locatingFace.description",
          info: "configurator.carriage.step.locatingFace.info",
          footerInfo: ["configurator.carriage.step.locatingFace.footerInfo"],
          tiles: [],
          tilesPerRow: 4,
          csticsToReset: [CarriageCstic.LocatingFace],
          visible: true,
        },
        {
          type: SubstepType.ConfigureCarriage,
          name: "configurator.carriage.step.configureCarriage.name",
          tiles: [],
          tilesPerRow: 4,
          visible: true,

          extraTexts: {
            sealingAndLubricationSectionName:
              "configurator.carriage.step.configureCarriage.section.sealingAndLubrication.name",
            sealingAndLubricationSectionInfo:
              "configurator.carriage.step.configureCarriage.section.sealingAndLubrication.info",
            lubricationConnectorSectionName: "configurator.carriage.cstic.lubricationConnector.name",
            lubricationConnectorSectionInfo:
              "configurator.carriage.step.configureCarriage.section.lubricationConnector.info",
            lubricationConnectorManualSectionName:
              "configurator.carriage.step.configureCarriage.section.lubricationConnector.manual.name",
            lubricationConnectorCentralSectionName:
              "configurator.carriage.step.configureCarriage.section.lubricationConnector.central.name",
            edit: "configurator.carriage.step.configureCarriage.btn.edit",
          },
        },
      ],
    },
    {
      name: "configurator.carriage.page.4.name",
      enabled: true,
      resetLabel: "",
      resetRequiresConfirmation: false,
      substeps: [
        {
          type: SubstepType.SummaryPage,
          visible: true,
          info: [
            {
              condition: ({ mainKmat }) => {
                return mainKmat.cstics[CarriageCstic.LocatingFace]?.value === "U" && userIsCustomer();
              },
              value: "configurator.carriage.summary.info.below.business",
            },
            {
              condition: ({ mainKmat }) => {
                return mainKmat.cstics[CarriageCstic.LocatingFace]?.value === "O" && userIsCustomer();
              },
              value: "configurator.carriage.summary.info.above.business",
            },
            {
              condition: ({ mainKmat }) => {
                return mainKmat.cstics[CarriageCstic.LocatingFace]?.value === "U";
              },
              value: "configurator.carriage.summary.info.below.plus",
            },
            {
              condition: ({ mainKmat }) => {
                return mainKmat.cstics[CarriageCstic.LocatingFace]?.value === "O";
              },
              value: "configurator.carriage.summary.info.above.plus",
            },
          ],
        },
      ],
    },
  ],
};
